import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import get from "lodash/get";

import { queryClient } from "App";
import { GROUPS_TABLE_CONSTANTS } from "routes/admin/components/groups_table/GroupsTableV3";
import { getGroupsTableColumns } from "routes/admin/components/groups_table/GroupsTableColumnsAndFilters";
import UserService from "services/User";
import GroupService from "services/Group";
import { addResizedColumns } from "hooks/useTableConfig";

export const invalidateGroupsQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [GROUPS_TABLE_CONSTANTS.queryKey],
  });
};

export const deleteGroup = async id => {
  await GroupService.delete(id);
  await invalidateGroupsQuery();
};

export const useGroupsMutations = () => {
  const create = useMutation({
    mutationFn: data => GroupService.create(data),
    onSuccess: () => invalidateGroupsQuery(),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => GroupService.update(id, data),
    onSuccess: () => invalidateGroupsQuery(),
  });

  return { create, update };
};

export const useGroupsTable = () => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getGroupsTableColumns();
    const groupsTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${GROUPS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    const withResizedColumns = addResizedColumns({
      columns: initialColumns,
      resizedColumns: groupsTableSettings?.resized_columns || {},
    });
    return withResizedColumns;
  }, [tableSettings, isTableSettingsFetched]);

  return { columns };
};
